import { useFlag, useNewCode } from '@calendly/feature-check-react';
import { useOnValueChange, usePrevious, useQueryParams } from '@calendly/hooks';
import { I18n } from '@calendly/i18n';
import { LongLoader } from '@calendly/ui/components/loader';
import { PageHeader } from '@calendly/ui/components/page-header';
import classNames from 'classnames';
import { isEmpty } from 'lodash-es';
import { createContext, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState, forwardRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Route, Switch, useLocation, useRouteMatch } from 'react-router-dom';
import { setGetParams } from 'actions/core/navigation';
import { MeetingsDebugMenu, useForceSSEDisconnect, useRealtimeSSEIntegration } from 'components/meetings';
import { useGetUserSettingsQuery } from 'features/availability';
import { BILLING_ENTRYPOINTS, isAvailableFeatureSelector } from 'features/billing';
import { CONTENT_PAGES } from 'features/empty_state';
import { AutoShareDiscoveryModal, useShouldDisplayAutoShareDiscoveryModal, FreeRecapsHeaderAction } from 'features/meeting_intelligence';
import { useMeetingsRealtimeFeaturesEnabled } from 'features/meetings/hooks';
import { SearchFilterExport } from 'features/meetings/search_filter_export';
import { useRightRail } from 'features/right_rail';
import { URL_STATE_PREFIX } from 'features/scoped_url_state';
import { DismissableNotetakerBanner } from 'features/upgrade_banner';
import { useTrackPageExit, useFeatureFlagEncountered, useTempFeatureFlagEncountered } from 'hooks/analytics_events';
import { useScope } from 'hooks/meetings/use_scope';
import { AddNotetakerModal } from './add_notetaker_modal';
import { AllPagesAuthorization, AllUsersAuthorization, PageAuthorization, UserAuthorization, UserGroupAuthorization } from './authorization';
import { MeetingsProvider } from './contexts/MeetingsContext';
import { ErrorList, EmptyFilteredList } from './error_states';
import { useMeetingsSearchData } from './hooks/search_data/use_search_data';
import { useRescheduledToast } from './hooks/use_rescheduled_toast';
import { useSelectedView } from './hooks/use_selected_view';
import { MeetingList } from './meeting_list';
import { MeetingsPageOnboardingTour } from './meetings_page_onboarding_tour';
import { isFilterSet } from './utils';
import { EMPTY_STATE_TYPES, EmptyState } from '../../components/empty_state';
import { normalizePersistedSchedulingMethods } from '../../constants/meetings';
import { useHomeTabs } from '../../hooks/home/tabs';
import { useFilters } from '../../hooks/meetings/filters';
const loadingWrapperCss = "l3hng8s";
const LoadingWrapper = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(loadingWrapperCss, props.className)} />;
});

/**
 * Provided by MeetingsPageContentWithRealtime / WithoutRealtime.
 * Carries meetingsData plus shared args (scope, showBuffers, defaultSchedulingFilters) so
 * MeetingsPageContentBody does not duplicate useScope/useGetUserSettingsQuery/derivations.
 */
const MeetingsPageSearchDataContext = createContext(null);
const InnerContent = ({
  data: meetings,
  hasNext,
  hasPrevious,
  headerRef,
  isError,
  isLoading,
  isNextLoading,
  isPreviousLoading,
  isSuccess,
  retrieveNextPage,
  retrievePreviousPage,
  selectedMeetingId,
  showBuffers,
  filterDefaults,
  onboardingTourMeetingRefCallback,
  onboardingTourNotetakerRefCallback,
  onVisibleDateRangeChange
}) => {
  const {
    getTabPathByName
  } = useHomeTabs();
  const {
    getFilterParams,
    applyFilterParams
  } = useFilters();
  const filterParams = getFilterParams(filterDefaults);

  // Only show error after the first load attempt has completed (avoids "Something went wrong" flash).
  const hasLoadAttemptStartedRef = useRef(false);
  useEffect(() => {
    if (isLoading) hasLoadAttemptStartedRef.current = true;
  }, [isLoading]);
  const hasLoadAttemptCompleted = hasLoadAttemptStartedRef.current && !isLoading;
  if (isLoading) {
    return <LoadingWrapper>
        <LongLoader data-testid="meeting-list-loader" />
      </LoadingWrapper>;
  }
  if ((isError || !isSuccess) && hasLoadAttemptCompleted) {
    return <ErrorList />;
  }
  if (isEmpty(meetings) && isFilterSet(filterParams)) {
    return <EmptyFilteredList apply={applyFilterParams} filterDefaults={filterDefaults} />;
  }
  if (isEmpty(meetings) && !isFilterSet(filterParams)) {
    return <EmptyState type={EMPTY_STATE_TYPES.leftRight} contentKey={CONTENT_PAGES.meetings} options={{
      url: getTabPathByName('event_types')
    }} />;
  }
  return <MeetingList meetings={meetings} retrieveNextPage={retrieveNextPage} retrievePreviousPage={retrievePreviousPage} selectedMeetingId={selectedMeetingId} showBuffers={showBuffers} hasNext={hasNext} hasPrevious={hasPrevious} isNextLoading={isNextLoading} isPreviousLoading={isPreviousLoading} headerRef={headerRef} onboardingTourMeetingRefCallback={onboardingTourMeetingRefCallback} onboardingTourNotetakerRefCallback={onboardingTourNotetakerRefCallback} onVisibleDateRangeChange={onVisibleDateRangeChange} />;
};

// Builds useMeetingsSearchData args from scope/settings; used by both realtime and legacy wrappers.
function useSearchDataArgs() {
  const {
    scopeType,
    scopeId
  } = useScope();
  const {
    data: userSettings
  } = useGetUserSettingsQuery();
  const meetingPageSettings = userSettings?.meetingsPageSettings || {};
  const defaultSchedulingFilters = useMemo(() => ({
    scheduling_methods: normalizePersistedSchedulingMethods(meetingPageSettings.schedulingMethods)
  }), [meetingPageSettings.schedulingMethods]);
  const showBuffers = meetingPageSettings.showBuffers ?? true;
  return {
    scopeType,
    scopeId,
    showBuffers,
    defaultFilters: defaultSchedulingFilters
  };
}

/**
 * The Contact Details Page has a meetings feed that tries to mirror the meetings page.
 * Changes to the meetings page should be reflected in the contact details page.
 * TODO: Refactor the meetings feed to use the meetings components and remove the duplicate code.
 * TODO: Add tests for the meetings feed.
 *
 * ref: javascripts/features/crm/contact_page/feed/meetings_feed.js
 */

/** Realtime path: SSE + polling enabled, uses search API for list data + visible-range polling. */
const MeetingsPageContentWithRealtime = () => {
  const args = useSearchDataArgs();
  const forceSSEDisconnect = useForceSSEDisconnect();
  const {
    connectionState
  } = useRealtimeSSEIntegration({
    enabled: !forceSSEDisconnect
  });
  const [visibleDateRange, setVisibleDateRange] = useState({
    start: null,
    end: null
  });
  const meetingsData = useMeetingsSearchData({
    ...args,
    pollingConfig: {
      isPollingEnabled: true
    },
    visibleRangePollingEnabled: true,
    visibleDateRange
  });
  const onVisibleDateRangeChange = useCallback(range => {
    setVisibleDateRange(range || {
      start: null,
      end: null
    });
  }, []);
  const contextValue = useMemo(() => ({
    meetingsData,
    scopeType: args.scopeType,
    scopeId: args.scopeId,
    showBuffers: args.showBuffers,
    defaultSchedulingFilters: args.defaultFilters,
    onVisibleDateRangeChange,
    connectionState
  }), [meetingsData, args.scopeType, args.scopeId, args.showBuffers, args.defaultFilters, onVisibleDateRangeChange, connectionState]);
  return <MeetingsPageSearchDataContext.Provider value={contextValue}>
      <MeetingsPageContentBody />
    </MeetingsPageSearchDataContext.Provider>;
};

/** Legacy path: no SSE, no polling, uses search API for list data. */
const MeetingsPageContentWithoutRealtime = () => {
  const args = useSearchDataArgs();
  const meetingsData = useMeetingsSearchData({
    ...args,
    pollingConfig: {
      isPollingEnabled: false
    }
  });
  const contextValue = useMemo(() => ({
    meetingsData,
    scopeType: args.scopeType,
    scopeId: args.scopeId,
    showBuffers: args.showBuffers,
    defaultSchedulingFilters: args.defaultFilters
  }), [meetingsData, args.scopeType, args.scopeId, args.showBuffers, args.defaultFilters]);
  return <MeetingsPageSearchDataContext.Provider value={contextValue}>
      <MeetingsPageContentBody />
    </MeetingsPageSearchDataContext.Provider>;
};
const MeetingsPageContentBody = () => {
  const dispatch = useDispatch();
  const {
    pathname
  } = useLocation();
  const previousPathname = usePrevious(pathname);
  const searchDataContext = useContext(MeetingsPageSearchDataContext);
  const {
    meetingsData,
    scopeType,
    scopeId,
    showBuffers,
    defaultSchedulingFilters: filterDefaults,
    onVisibleDateRangeChange,
    connectionState
  } = searchDataContext ?? {};
  const isRealtimeFeatureFlagOn = useFlag('meetings_recaps_realtime_feature');
  const scopeKey = `${scopeType}-${scopeId}`;
  const previousScopeKey = usePrevious(scopeKey);
  const selectedView = useSelectedView();
  const headerRef = useRef(null);
  const onboardingTourMeetingRef = useRef(null);
  const onboardingTourNotetakerRef = useRef(null);
  const onboardingTourSettingsRef = useRef(null);
  const [isMeetingRefReady, setMeetingRefReady] = useState(false);
  const [isNotetakerRefReady, setNotetakerRefReady] = useState(false);
  const [isSettingsRefReady, setSettingsRefReady] = useState(false);
  const onboardingTourMeetingRefCallback = useCallback(el => {
    onboardingTourMeetingRef.current = el;
    setMeetingRefReady(!!el);
  }, []);
  const onboardingTourNotetakerRefCallback = useCallback(el => {
    onboardingTourNotetakerRef.current = el;
    setNotetakerRefReady(!!el);
  }, []);
  const onboardingTourSettingsRefCallback = useCallback(el => {
    onboardingTourSettingsRef.current = el;
    setSettingsRefReady(!!el);
  }, []);
  const isFilterSettingsEnabled = useNewCode('sa_filter_settings_mtg_pg', false)?.enabled;
  const hasOpenedInitialMeeting = useRef(false);
  const justClearedScopeRef = useRef(false);
  const queryParams = useQueryParams();
  const {
    debug,
    selected,
    share_id: shareId,
    show_add_notetaker_modal: showAddNotetakerModalParam
  } = queryParams;
  const shouldShowAddNotetakerModal = showAddNotetakerModalParam === 'true';
  useRescheduledToast();
  const {
    openOrReplace,
    close
  } = useRightRail();
  const openOrReplaceRef = useRef(openOrReplace);
  const closeRef = useRef(close);
  openOrReplaceRef.current = openOrReplace;
  closeRef.current = close;
  useShouldDisplayAutoShareDiscoveryModal();

  // Track feature flag encounters that determine which meetings page to show. Update this when the flags change.
  const hasNewMeetingPage = useFlag('sa_new_meetings_page');
  useFeatureFlagEncountered(hasNewMeetingPage ? 'sa_new_meetings_page' : 'meeting_intelligence');
  useTempFeatureFlagEncountered('sa_new_meetings_page');
  useTrackPageExit({
    routePattern: '/app/meetings',
    properties: {
      title: 'Meetings'
    }
  });
  const eventId = selected;
  const featureKey = 'meeting_intelligence';
  const isFeatureAvailable = useSelector(state => isAvailableFeatureSelector(state, {
    featureKey
  }));
  const isWizardAcknowledgementModalVisible = useSelector(state => state.meetingIntelligence?.acknowledgementModal?.showModal === true);
  const selectedMeeting = useMemo(() => {
    if (!eventId || !meetingsData?.data?.length) {
      return null;
    }
    for (const day of meetingsData.data) {
      const match = day.events?.find(event => event?.uuid === eventId);
      if (match) {
        return match;
      }
    }
    return null;
  }, [eventId, meetingsData?.data]);
  const clearedInvalidSelectedRef = useRef(null);

  // When meeting is not in the list, only clear the add-notetaker modal flag.
  // Do not clear selected/share_id here: the right rail stays open so the meeting
  // detail provider can run REST then GraphQL fallback; it will clear selected when both fail.
  useEffect(() => {
    if (!eventId || !meetingsData?.data?.length) return;
    const found = meetingsData.data.some(day => day.events?.some(event => event?.uuid === eventId));
    if (found) return;
    if (clearedInvalidSelectedRef.current === eventId) return;
    clearedInvalidSelectedRef.current = eventId;
    dispatch(setGetParams({
      show_add_notetaker_modal: null
    }));
  }, [eventId, meetingsData?.data, dispatch]);
  const refreshMeeting = useCallback(meetingUuid => {
    if (meetingsData?.refreshMeeting) {
      meetingsData.refreshMeeting(meetingUuid);
    }
  }, [meetingsData]);
  const clearActiveMeeting = useCallback(() => {
    dispatch(setGetParams({
      selected: null,
      share_id: null
    }));
  }, [dispatch]);
  const handleCloseAddNotetakerModal = useCallback(() => {
    dispatch(setGetParams({
      show_add_notetaker_modal: null
    }));
  }, [dispatch]);

  // When user/scope changes (pathname or scope dropdown), clear right rail and selection immediately.
  // useLayoutEffect so this runs before other effects; set justClearedScopeRef so we don't re-open
  // (openOrReplace would re-spread current URL params and put selected back).
  // Also call close() to sync the rail's internal stack - prevents "Maximum update depth exceeded"
  // when openOrReplace/close refs change after URL update and re-trigger effects.
  //
  // Exception: when URL has selected/share_id, do NOT clear. Scope can change spuriously when the
  // meetings list finishes loading (scopeKey may settle or re-derived values trigger scopeChanged).
  // User-initiated scope change strips selected before navigating (setTabScopeV1), so we never
  // have selected during a real scope switch. If we have selected, the provider is loading/showing
  // that meeting - don't close the rail.
  useLayoutEffect(() => {
    const pathChanged = previousPathname !== undefined && previousPathname !== pathname;
    const scopeChanged = previousScopeKey !== undefined && previousScopeKey !== scopeKey;
    if (!pathChanged && !scopeChanged) return;
    if (selected || shareId) {
      justClearedScopeRef.current = false;
      return;
    }
    justClearedScopeRef.current = true;
    closeRef.current({
      all: true
    });
    const prefix = URL_STATE_PREFIX.RIGHT_RAIL;
    dispatch(setGetParams({
      [prefix]: null,
      [`${prefix}State`]: null,
      selected: null,
      share_id: null
    }));
  }, [pathname, previousPathname, scopeKey, previousScopeKey, dispatch, selected, shareId]);

  // Open rail when we have a selection; reset "opened" flag when selection is cleared so the next
  // selection in this scope will open the rail again.
  // Use refs for openOrReplace/close - they change when URL updates (e.g. on scope clear), which
  // would re-trigger this effect and potentially re-open the rail, causing infinite update loop.
  useEffect(() => {
    if (!eventId && !shareId) {
      hasOpenedInitialMeeting.current = false;
      return;
    }
    if (justClearedScopeRef.current) {
      justClearedScopeRef.current = false;
      return;
    }
    if (!hasOpenedInitialMeeting.current) {
      openOrReplaceRef.current('meeting_details', {
        eventId,
        shareId
      });
      hasOpenedInitialMeeting.current = true;
    }
  }, [eventId, shareId]);
  useOnValueChange(eventId, () => {
    if (eventId || shareId) {
      if (justClearedScopeRef.current) {
        justClearedScopeRef.current = false;
        return;
      }
      openOrReplaceRef.current('meeting_details', {
        eventId,
        shareId
      });
    } else {
      closeRef.current();
    }
  });
  useOnValueChange(shareId, () => {
    if (eventId || shareId) {
      if (justClearedScopeRef.current) {
        justClearedScopeRef.current = false;
        return;
      }
      openOrReplaceRef.current('meeting_details', {
        eventId,
        shareId
      });
    } else {
      closeRef.current();
    }
  });
  const showMeetingsDebugMenu = isRealtimeFeatureFlagOn && debug === 'true';
  const headerActions = [...(FreeRecapsHeaderAction ? [<FreeRecapsHeaderAction key="free-recaps-header-action" paywallLocation={BILLING_ENTRYPOINTS.MEETINGS_PAGE} />] : []), ...(showMeetingsDebugMenu ? [<MeetingsDebugMenu key="meetings-debug-menu" connectionState={connectionState} />] : [])];
  return <MeetingsProvider meetings={meetingsData.data}>
      <PageHeader title={I18n.t('publisher.meetings.header')} actions={headerActions} />
      {!isFeatureAvailable && <DismissableNotetakerBanner bannerType="meetings" featureKey={featureKey} />}
      <SearchFilterExport scope={{
      id: scopeId,
      type: scopeType
    }} showBuffers={showBuffers} selectedView={selectedView} canExport ref={headerRef} clearActiveMeeting={clearActiveMeeting} filterDefaults={filterDefaults} onboardingTourSettingsRef={onboardingTourSettingsRefCallback} />
      <InnerContent showBuffers={showBuffers} headerRef={headerRef} selectedMeetingId={eventId} filterDefaults={filterDefaults} onboardingTourMeetingRefCallback={onboardingTourMeetingRefCallback} onboardingTourNotetakerRefCallback={onboardingTourNotetakerRefCallback} onVisibleDateRangeChange={onVisibleDateRangeChange} {...meetingsData} />
      {selectedMeeting && shouldShowAddNotetakerModal && <AddNotetakerModal isOpen={shouldShowAddNotetakerModal} onClose={handleCloseAddNotetakerModal} onMeetingUpdated={refreshMeeting} selectedMeeting={selectedMeeting} />}
      <AutoShareDiscoveryModal />
      <MeetingsPageOnboardingTour onboardingTourMeetingRef={onboardingTourMeetingRef} onboardingTourNotetakerRef={onboardingTourNotetakerRef} onboardingTourSettingsRef={onboardingTourSettingsRef} isMeetingRefReady={isMeetingRefReady} isNotetakerRefReady={isNotetakerRefReady} isSettingsRefReady={isSettingsRefReady} isFilterSettingsEnabled={isFilterSettingsEnabled} isWizardAcknowledgementModalVisible={isWizardAcknowledgementModalVisible} />
    </MeetingsProvider>;
};

/** Picks realtime (SSE + polling) vs legacy (no SSE, no polling) branch. */
const MeetingsPageContent = () => {
  const hasRealtimeFeaturesEnabled = useMeetingsRealtimeFeaturesEnabled();
  return hasRealtimeFeaturesEnabled ? <MeetingsPageContentWithRealtime /> : <MeetingsPageContentWithoutRealtime />;
};
export const MeetingsPage = () => {
  const {
    params
  } = useRouteMatch();
  const basePath = params[0];
  const {
    isLoading: isLoadingSettings,
    isError: isSettingsError
  } = useGetUserSettingsQuery();

  // Gate only on the very first settings load to avoid multiple search API calls.
  // Once settings have resolved (success or error), never show this loader again —
  // subsequent cache invalidations / refetches must not unmount MeetingsPageContent,
  // which would restart all queries and cause the meeting list to remount + scroll.
  const hasSettingsResolved = useRef(false);
  if (!isLoadingSettings || isSettingsError) {
    hasSettingsResolved.current = true;
  }
  if (!hasSettingsResolved.current) {
    return <LoadingWrapper>
        <LongLoader data-testid="meeting-list-loader" />
      </LoadingWrapper>;
  }
  return <Switch>
      <Route exact path={`${basePath}/user/me`}>
        <MeetingsPageContent />
      </Route>
      <Route exact path={`${basePath}/team`}>
        <AllPagesAuthorization>
          <MeetingsPageContent />
        </AllPagesAuthorization>
      </Route>
      <Route exact path={`${basePath}/all_users_and_teams`}>
        <AllUsersAuthorization>
          <MeetingsPageContent />
        </AllUsersAuthorization>
      </Route>
      <Route exact path={`${basePath}/all_users`}>
        <AllUsersAuthorization>
          <MeetingsPageContent />
        </AllUsersAuthorization>
      </Route>
      <Route exact path={`${basePath}/team/:team_id`}>
        <PageAuthorization>
          <MeetingsPageContent />
        </PageAuthorization>
      </Route>
      <Route exact path={`${basePath}/user_group/:user_group_id`}>
        <UserGroupAuthorization>
          <MeetingsPageContent />
        </UserGroupAuthorization>
      </Route>
      <Route exact path={`${basePath}/user/:user_id`}>
        <UserAuthorization>
          <MeetingsPageContent />
        </UserAuthorization>
      </Route>
      <Route>
        <MeetingsPageContent />
      </Route>
    </Switch>;
};

require("./meetings_page.wyw-in-js.css!=!../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./meetings_page.js");